import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import usePlans from "../usePlans";
import api from "../../services/api";
import { campaignEnabled, extraGuides, initialSteps, planFlag, planCapacity } from "./guide";
import { TRACK_EVENT, readTrack, saveTrack, sellSteps } from "./sellTrack";

export const GETTING_STARTED_EVENT = "gettingStartedUpdated";
export const GETTING_STARTED_STORAGE = "gettingStartedBadge";

export const publishGettingStartedBadge = (done, total) => {
  const payload = { done: Number(done) || 0, total: Number(total) || 0 };
  try {
    localStorage.setItem(GETTING_STARTED_STORAGE, JSON.stringify(payload));
  } catch (_) {
    /* noop */
  }
  window.dispatchEvent(new CustomEvent(GETTING_STARTED_EVENT, { detail: payload }));
};

export const readGettingStartedBadge = () => {
  try {
    const raw = localStorage.getItem(GETTING_STARTED_STORAGE);
    if (!raw) return { done: 0, total: 0 };
    const parsed = JSON.parse(raw);
    return {
      done: Number(parsed?.done) || 0,
      total: Number(parsed?.total) || 0,
    };
  } catch (_) {
    return { done: 0, total: 0 };
  }
};

const safeGet = async (fn) => {
  try {
    return await fn();
  } catch (_) {
    return null;
  }
};

const useGettingStarted = ({ includeExtras = true } = {}) => {
  const { user } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext) || { whatsApps: [] };
  const { getPlanCompany } = usePlans();
  const [loading, setLoading] = useState(true);
  const [queues, setQueues] = useState([]);
  const [userCount, setUserCount] = useState(1);
  const [planState, setPlanState] = useState(null);
  const plan = planState && planState.companyId === user?.companyId && planState.userId === user?.id ? planState.plan : null;
  const refreshVersion = useRef(0);
  const [canAccessAgenda, setCanAccessAgenda] = useState(false);
  const [quickCount, setQuickCount] = useState(0);
  const [tagCount, setTagCount] = useState(0);
  const [promptCount, setPromptCount] = useState(0);
  const [campaignCount, setCampaignCount] = useState(0);
  const [scheduleCount, setScheduleCount] = useState(0);
  const [csatEnabled, setCsatEnabled] = useState(false);
  const [contactCount, setContactCount] = useState(0);
  const [hasOptions, setHasOptions] = useState(false);
  const [hasAutomation, setHasAutomation] = useState(false);
  const [hasStore, setHasStore] = useState(false);
  const [groupCampaignCount, setGroupCampaignCount] = useState(0);
  const [statusCampaignCount, setStatusCampaignCount] = useState(0);
  const [sellRows, setSellRows] = useState(null);
  const [trackVersion, setTrackVersion] = useState(0);

  const isAdmin =
    user?.profile === "admin" ||
    user?.profile === "super" ||
    user?.super === true ||
    user?.super === 1 ||
    String(user?.super) === "1";

  const refresh = useCallback(async () => {
    const version = ++refreshVersion.current;
    setPlanState(null);
    setCanAccessAgenda(false);
    if (!user?.companyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const planRes = await safeGet(() => getPlanCompany(undefined, user.companyId));
      if (version !== refreshVersion.current) return;
      const currentPlan = planRes?.plan || null;
      if (!currentPlan) return;
      const [queueRes, usersRes, quickRes, tagsRes, promptsRes, campaignsRes, schedulesRes, settingsRes, agendaRes, contactsRes, flowsRes, integrationsRes, paymentsRes, productsRes, groupRes, statusRes] =
        await Promise.all([
          isAdmin && planCapacity(currentPlan, "queues") ? safeGet(() => api.get("/queue")) : Promise.resolve(null),
          isAdmin && Number(currentPlan.users) > 1 ? safeGet(() => api.get("/users/", { params: { searchParam: "", pageNumber: 1 } })) : Promise.resolve(null),
          includeExtras ? safeGet(() => api.get("/quick-messages", { params: { searchParam: "", pageNumber: 1 } })) : Promise.resolve(null),
          includeExtras ? safeGet(() => api.get("/tags/", { params: { searchParam: "", pageNumber: 1 } })) : Promise.resolve(null),
          includeExtras && isAdmin && planFlag(currentPlan, "useOpenAi") ? safeGet(() => api.get("/prompt")) : Promise.resolve(null),
          includeExtras && isAdmin && planFlag(currentPlan, "useCampaigns") ? safeGet(() => api.get("/campaigns/", { params: { searchParam: "", pageNumber: 1 } })) : Promise.resolve(null),
          includeExtras && planFlag(currentPlan, "useSchedules") ? safeGet(() => api.get("/schedules/", { params: { searchParam: "", pageNumber: 1 } })) : Promise.resolve(null),
          isAdmin ? safeGet(() => api.get("/settings")) : Promise.resolve(null),
          includeExtras ? safeGet(() => api.get("/users/can-access-agenda")) : Promise.resolve(null),
          isAdmin ? safeGet(() => api.get("/contacts/count")) : Promise.resolve(null),
          isAdmin ? safeGet(() => api.get("/flowbuilders", { params: { searchParam: "", pageNumber: 1, rowsPerPage: 1 } })) : Promise.resolve(null),
          isAdmin && planFlag(currentPlan, "useIntegrations") ? safeGet(() => api.get("/queueIntegration/", { params: { searchParam: "", pageNumber: 1 } })) : Promise.resolve(null),
          isAdmin && planFlag(currentPlan, "useNegocio") ? safeGet(() => api.get("/negocio/payment-methods")) : Promise.resolve(null),
          isAdmin && planFlag(currentPlan, "useNegocio") ? safeGet(() => api.get("/negocio/products")) : Promise.resolve(null),
          isAdmin && campaignEnabled(currentPlan, "useGroupCampaigns") ? safeGet(() => api.get("/group-campaigns")) : Promise.resolve(null),
          isAdmin && campaignEnabled(currentPlan, "useStatusCampaigns") ? safeGet(() => api.get("/status-campaigns")) : Promise.resolve(null),
        ]);

      if (version !== refreshVersion.current) return;
      setPlanState({ companyId: user.companyId, userId: user.id, plan: currentPlan });
      // Ruta «Vender servicios digitales»: estado real de cada paso desde el backend.
      if (isAdmin && planFlag(currentPlan, "useNegocio") && readTrack(user.companyId, true) === "sell") {
        const sell = await safeGet(() => api.get("/negocio/getting-started"));
        if (version !== refreshVersion.current) return;
        setSellRows(Array.isArray(sell?.data?.steps) ? sell.data.steps : null);
      } else {
        setSellRows(null);
      }
      setQueues(Array.isArray(queueRes?.data) ? queueRes.data : []);
      const users = usersRes?.data?.users || usersRes?.data || [];
      setUserCount(Number(usersRes?.data?.count) || (Array.isArray(users) ? users.length : 1));
      setQuickCount(
        Array.isArray(quickRes?.data?.records)
          ? quickRes.data.records.length
          : Array.isArray(quickRes?.data)
          ? quickRes.data.length
          : 0
      );
      setTagCount(Array.isArray(tagsRes?.data?.tags) ? tagsRes.data.tags.length : 0);
      const prompts = promptsRes?.data?.prompts || promptsRes?.data || [];
      setPromptCount(Array.isArray(prompts) ? prompts.length : 0);
      setCampaignCount(
        Array.isArray(campaignsRes?.data?.records)
          ? campaignsRes.data.records.length
          : Array.isArray(campaignsRes?.data)
          ? campaignsRes.data.length
          : 0
      );
      setScheduleCount(Array.isArray(schedulesRes?.data?.schedules) ? schedulesRes.data.schedules.length : 0);
      const settings = settingsRes?.data || [];
      const rating = Array.isArray(settings)
        ? settings.find((s) => s.key === "userRating")
        : null;
      setCsatEnabled(rating?.value === "enabled");
      const settingValue = (key) => (Array.isArray(settings) ? settings.find((row) => row.key === key)?.value : null);
      setHasOptions(["userRating", "sendGreetingAccepted", "call"].some((key) => settingValue(key) === "enabled") || settingValue("scheduleType") === "company");
      setContactCount(Number(contactsRes?.data?.count) || 0);
      const countOf = (payload) => {
        if (Array.isArray(payload)) return payload.length;
        if (Array.isArray(payload?.records)) return payload.records.length;
        if (Array.isArray(payload?.queueIntegrations)) return payload.queueIntegrations.length;
        return Number(payload?.count) || 0;
      };
      const flows = countOf(flowsRes?.data);
      const integrations = countOf(integrationsRes?.data);
      setHasAutomation((Array.isArray(prompts) ? prompts.length : 0) > 0 || flows > 0 || integrations > 0);
      setHasStore(countOf(paymentsRes?.data) > 0 || countOf(productsRes?.data) > 0);
      setGroupCampaignCount(countOf(groupRes?.data));
      setStatusCampaignCount(countOf(statusRes?.data));
      if (agendaRes?.data?.canAccessAgenda !== undefined) {
        setCanAccessAgenda(!!agendaRes.data.canAccessAgenda);
      }
    } finally {
      if (version === refreshVersion.current) setLoading(false);
    }
  }, [includeExtras, isAdmin, user?.companyId, user?.id, trackVersion]);

  // Cambio de ruta (en esta pestaña o desde la página): recalcular pasos y contador.
  useEffect(() => {
    const onTrack = () => setTrackVersion((v) => v + 1);
    window.addEventListener(TRACK_EVENT, onTrack);
    return () => window.removeEventListener(TRACK_EVENT, onTrack);
  }, []);

  useEffect(() => {
    refresh();
    const reload = () => refresh();
    window.addEventListener("planConfigUpdated", reload);
    window.addEventListener("focus", reload);
    return () => {
      ++refreshVersion.current;
      window.removeEventListener("planConfigUpdated", reload);
      window.removeEventListener("focus", reload);
    };
  }, [refresh]);

  const connections = Array.isArray(whatsApps) ? whatsApps.filter(w => w.companyId == null || Number(w.companyId) === Number(user?.companyId)) : [];
  const hasConnection = connections.some(connection => String(connection.status).toUpperCase() === "CONNECTED");
  const hasQueue = queues.length > 0;
  const hasGreeting = queues.some((q) => String(q.greetingMessage || "").trim().length > 0);
  const hasInvite = userCount > 1;
  const hasAssignment = connections.some((w) => Array.isArray(w.queues) && w.queues.length > 0);

  const hasNegocio = !!plan && isAdmin && planFlag(plan, "useNegocio");
  const track = readTrack(user?.companyId, hasNegocio);
  const attentionSteps = useMemo(() => initialSteps(plan, isAdmin, {
    hasConnection, hasQueue, hasGreeting, hasInvite, hasAssignment,
    quickCount, tagCount, contactCount, hasOptions, hasAutomation, hasStore,
    groupCampaignCount, statusCampaignCount, campaignCount,
  }), [plan, isAdmin, hasConnection, hasQueue, hasGreeting, hasInvite, hasAssignment, quickCount, tagCount, contactCount, hasOptions, hasAutomation, hasStore, groupCampaignCount, statusCampaignCount, campaignCount]);
  const steps = useMemo(
    () => (track === "sell" && sellRows ? sellSteps(sellRows) : attentionSteps),
    // trackVersion: readTrack lee localStorage, se recalcula al cambiar de ruta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [track, sellRows, attentionSteps, trackVersion]
  );
  const extras = useMemo(() => includeExtras ? extraGuides(plan, isAdmin, { quickCount, tagCount, promptCount, campaignCount, scheduleCount, canAccessAgenda, hasInvite, csatEnabled }) : [],
    [plan, isAdmin, includeExtras, quickCount, tagCount, promptCount, campaignCount, scheduleCount, canAccessAgenda, hasInvite, csatEnabled]);

  const requiredSteps = steps.filter((step) => !step.optional);
  const done = requiredSteps.filter((step) => step.done).length;
  const total = requiredSteps.length;
  const nextStep = requiredSteps.find((step) => !step.done) || null;

  useEffect(() => {
    if (!loading && isAdmin) {
      publishGettingStartedBadge(done, total);
    } else if (!loading && !isAdmin) {
      publishGettingStartedBadge(0, 0);
    }
  }, [done, isAdmin, loading, total]);

  return {
    loading,
    planError: !loading && !!user?.companyId && !plan,
    isAdmin,
    steps,
    extras,
    done,
    total,
    nextStep,
    refresh,
    plan,
    track,
    canChooseTrack: hasNegocio,
    setTrack: (next) => saveTrack(user?.companyId, next),
    userName: user?.name || "Admin",
  };
};

export default useGettingStarted;
